import type { Product } from "../../../mocks/products";
import { CATEGORIES } from "../../../mocks/products";

interface ProductTableProps {
  products: Product[];
  onEdit: (product: Product) => void;
  onDelete: (product: Product) => void;
}

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <i key={s} className={`text-xs ${s <= Math.round(rating) ? "ri-star-fill text-amber-400" : "ri-star-line text-gray-300"}`} />
      ))}
      <span className="ml-1 text-gray-400 text-xs">{rating}</span>
    </div>
  );
}

export default function ProductTable({ products, onEdit, onDelete }: ProductTableProps) {
  const getCategoryLabel = (id: string) => CATEGORIES.find((c) => c.id === id)?.label ?? id;

  if (products.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="w-16 h-16 flex items-center justify-center bg-gray-50 rounded-2xl mb-4">
          <i className="ri-inbox-line text-3xl text-gray-300"></i>
        </div>
        <p className="text-gray-400 text-sm">沒有找到符合的商品</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[900px]">
        <thead>
          <tr className="border-b border-gray-100 bg-gray-50">
            <th className="px-5 py-3 text-left text-xs font-semibold text-gray-400 tracking-wider uppercase w-20">縮圖</th>
            <th className="px-5 py-3 text-left text-xs font-semibold text-gray-400 tracking-wider uppercase">商品名稱</th>
            <th className="px-5 py-3 text-left text-xs font-semibold text-gray-400 tracking-wider uppercase w-24">分類</th>
            <th className="px-5 py-3 text-left text-xs font-semibold text-gray-400 tracking-wider uppercase w-32">現價</th>
            <th className="px-5 py-3 text-left text-xs font-semibold text-gray-400 tracking-wider uppercase w-20">折扣</th>
            <th className="px-5 py-3 text-left text-xs font-semibold text-gray-400 tracking-wider uppercase w-36">評分</th>
            <th className="px-5 py-3 text-center text-xs font-semibold text-gray-400 tracking-wider uppercase w-24">操作</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {products.map((product) => (
            <tr key={product.id} className="hover:bg-gray-50/70 transition-colors duration-100">
              {/* Thumbnail */}
              <td className="px-5 py-4">
                <div className="w-14 h-14 rounded-xl overflow-hidden bg-gray-100 flex-shrink-0">
                  <img src={product.image} alt={product.name} className="w-full h-full object-cover object-top" />
                </div>
              </td>

              {/* Name */}
              <td className="px-5 py-4">
                <p className="text-[10px] text-gray-400 tracking-widest uppercase mb-0.5 font-medium">{product.brand}</p>
                <p className="text-gray-800 text-sm font-semibold leading-snug line-clamp-2 max-w-xs">{product.name}</p>
                <p className="text-gray-400 text-xs mt-1 line-clamp-1">{product.description}</p>
              </td>

              {/* Category */}
              <td className="px-5 py-4">
                <span className="inline-flex items-center px-2.5 py-1 bg-[#1B4332]/8 text-[#1B4332] text-xs font-medium rounded-full whitespace-nowrap">
                  {getCategoryLabel(product.category)}
                </span>
              </td>

              {/* Price */}
              <td className="px-5 py-4">
                <p className="text-gray-800 text-sm font-bold">NT$ {product.price.toLocaleString()}</p>
                <p className="text-gray-400 text-xs line-through mt-0.5">NT$ {product.originalPrice.toLocaleString()}</p>
              </td>

              {/* Discount */}
              <td className="px-5 py-4">
                <span className="inline-flex items-center justify-center w-12 h-6 bg-red-50 text-red-500 text-xs font-bold rounded-full">
                  -{product.discount}%
                </span>
              </td>

              {/* Rating */}
              <td className="px-5 py-4">
                <StarRating rating={product.rating} />
                <p className="text-gray-400 text-xs mt-1">{product.reviewCount.toLocaleString()} 則</p>
              </td>

              {/* Actions */}
              <td className="px-5 py-4">
                <div className="flex items-center justify-center gap-1.5">
                  <button
                    onClick={() => onEdit(product)}
                    className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-[#1B4332] hover:bg-[#1B4332]/8 rounded-lg transition-all duration-150 cursor-pointer"
                    title="編輯"
                  >
                    <i className="ri-edit-line text-sm"></i>
                  </button>
                  <button
                    onClick={() => onDelete(product)}
                    className="w-8 h-8 flex items-center justify-center text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all duration-150 cursor-pointer"
                    title="刪除"
                  >
                    <i className="ri-delete-bin-line text-sm"></i>
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
